/**
 * Terminal-session handling for the in-person recording page, kept apart from the React hook so the decisions can
 * be unit-tested (there is no jsdom).
 */
import { MeetingSessionStatusCode } from 'amazon-chime-sdk-js';
import type { InPersonHostStatus } from './inPersonRecordingTypes';

export const IN_PERSON_TAKEN_OVER_TEXT =
  'This recording continued on another device; this device is no longer recording.';
export const IN_PERSON_ENDED_ELSEWHERE_TEXT =
  'This recording session ended or continued on another device; this device is no longer recording.';
export const IN_PERSON_RECONNECTING_TEXT = 'Recording connection lost — reconnecting…';
export const IN_PERSON_RECONNECT_FAILED_TEXT =
  'Recording connection lost and could not reconnect. This room is not being recorded.';

export type InPersonDisconnectAction = 'none' | 'taken-over' | 'ended-elsewhere' | 'rejoin';

/**
 * What to do when the media session ends in `error` while this page is recording.
 *  - `AudioAttendeeRemoved`: the practitioner pressed Start on another device and `mintAttendee` evicted this one.
 *    Rejoining would evict that device in turn, so stop here instead.
 *  - An eviction can also arrive as `ended` with `endedByHost` set (`MeetingEnded` / `TURNCredentialsForbidden`): the
 *    room was ended or taken over elsewhere. Treated like a takeover: stop locally, never rejoin.
 *  - Any other terminal code (an exhausted reconnect and so on): rejoin, as the wake-up path does.
 */
export function decideInPersonDisconnect(input: {
  hostStatus: InPersonHostStatus;
  sessionStatus: string;
  errorCode: number | undefined;
  endedByHost?: boolean;
  /**
   * An End is in flight. The bot deletes the meeting before it answers, so the SDK reports `MeetingEnded`
   * (`endedByHost`) while the page is still `joined`; that is this device's own End, not an eviction.
   */
  ending?: boolean;
}): InPersonDisconnectAction {
  if (input.ending) return 'none';
  if (input.hostStatus === 'joined' && input.sessionStatus === 'ended' && input.endedByHost) return 'ended-elsewhere';
  if (input.hostStatus !== 'joined' || input.sessionStatus !== 'error') return 'none';
  return input.errorCode === MeetingSessionStatusCode.AudioAttendeeRemoved ? 'taken-over' : 'rejoin';
}

export type InPersonReconnectState = 'idle' | 'reconnecting' | 'failed';

/**
 * The persistent reconnect notice: shown from the loss until the session is back in the meeting. A failed rejoin
 * keeps it, as an error, so the provider knows the room is not being captured. The 5-second `session-status` poll
 * never touches it (it only drives the separate "recording stopped" notice).
 */
export function inPersonReconnectNotice(state: InPersonReconnectState, sessionStatus: string): string | null {
  if (state === 'idle' || sessionStatus === 'in-meeting') return null;
  return state === 'failed' ? IN_PERSON_RECONNECT_FAILED_TEXT : IN_PERSON_RECONNECTING_TEXT;
}

export type InPersonDisconnectHandlers = {
  /** Stop local media and release the wake lock. */
  stopLocally: () => void;
  /** Taken over or ended elsewhere: leave the joined state for a terminal one that shows `text`. */
  onTakenOver: (text: string) => void;
  /** The same rejoin as the wake-up path; rejects when it fails. */
  rejoin: () => Promise<unknown>;
  onReconnectState: (state: InPersonReconnectState) => void;
  onRejoinError: (e: unknown) => void;
};

/** Runs the decided action. A rejoin that fails leaves the reconnect state `failed` (the notice persists). */
export async function runInPersonDisconnect(
  action: InPersonDisconnectAction,
  handlers: InPersonDisconnectHandlers,
): Promise<void> {
  if (action === 'taken-over' || action === 'ended-elsewhere') {
    handlers.stopLocally();
    handlers.onTakenOver(action === 'taken-over' ? IN_PERSON_TAKEN_OVER_TEXT : IN_PERSON_ENDED_ELSEWHERE_TEXT);
    return;
  }
  if (action !== 'rejoin') return;
  handlers.onReconnectState('reconnecting');
  try {
    await handlers.rejoin();
  } catch (e) {
    handlers.onReconnectState('failed');
    handlers.onRejoinError(e);
  }
}

/**
 * The "recording stopped" notice (Decision 39): the page is in the meeting and Chime's roster has no recorder (a
 * join's `recordingActive` holds until the recorder shows, at most `CHIME_RECORDER_SETTLE_MS`). Not while
 * reconnecting: the reconnect notice covers that.
 */
export function inPersonRecordingStopped(input: {
  hostStatus: InPersonHostStatus;
  sessionStatus: string;
  recording: boolean;
}): boolean {
  return input.hostStatus === 'joined' && input.sessionStatus === 'in-meeting' && !input.recording;
}
